import { setWithSpread, unsetWithSpread } from "@client/lib/object";
import type { GenericMap, WithChildren } from "@common/declarations";
import { identity } from "lodash";
import React, { type Reducer } from "react";
import { useStore } from "react-redux";

export type NextContextProps = {
  injectReducer: (path: string, reducer: Reducer<any, any>) => void;
  removeReducer: (path: string) => void;
};

const NextContext = React.createContext<NextContextProps>({
  injectReducer: identity,
  removeReducer: identity
});
export default NextContext;

export type NextContextCreateReducer = (dynamicReducers: GenericMap<any>) => Reducer<any, any>;

type Props = WithChildren & {
  createReducer: NextContextCreateReducer;
  dynamicReducers: GenericMap<any>;
};
export const NextContextProvider = (props: Props) => {
  const store = useStore();

  const reducers = React.useRef<GenericMap<any>>(props.dynamicReducers);

  React.useLayoutEffect(() => {
    store.replaceReducer(props.createReducer(reducers.current));
  }, []);

  const value = React.useMemo(
    (): NextContextProps => ({
      injectReducer: (path: string, reducer: Reducer<any, any>) => {
        if (path in reducers.current) return;

        reducers.current = setWithSpread(reducers.current, path, reducer);
        store.replaceReducer(props.createReducer(reducers.current));
      },
      removeReducer: (path: string) => {
        reducers.current = unsetWithSpread(reducers.current, path);
        store.replaceReducer(props.createReducer(reducers.current));
      }
    }),
    [props.createReducer]
  );

  return <NextContext.Provider value={value}>{props.children}</NextContext.Provider>;
};
